import { useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// react-pdf needs its worker script served. We deliberately do NOT use
// webpack 5's `new URL(..., import.meta.url)` asset pipeline here (which
// would emit a content-hashed `/static/js/*.mjs` file) — some live static
// hosts/CDNs don't recognize the `.mjs` extension as a servable static
// asset at all and silently fall back to serving `index.html` for it
// (confirmed: a live deployment correctly 404s a missing `.js` file, but
// returns 200 HTML for ANY `.mjs` path, existing or not) — which crashes
// pdf.js before it ever attempts to fetch the actual PDF. Copying the
// self-contained worker bundle into `public/` as a plain `.js` file (see
// `pdf.worker.min.js` there) sidesteps that entirely: the browser's
// `new Worker(url, {type: "module"})` call (used internally by pdfjs-dist)
// doesn't care about file extension, only that the response is valid JS.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// Some live-environment reverse proxies/CDNs mishandle pdf.js's HTTP Range
// (byte-range) probing/requests for this dynamic, token-gated PDF endpoint —
// corrupting or 416/404-ing the response. Forcing a single full-file
// download sidesteps that entirely. Module-level constant so its identity
// never changes across renders (a fresh object here would be treated by
// react-pdf the same way an unstable `file` prop is — see note below).
const PDF_LOAD_OPTIONS = { disableRange: true, disableStream: true };

/**
 * Renders one page of a PDF (admin box-placement + signer view share this).
 * `overlays` are absolutely-positioned children (percentage-based) drawn on
 * top — signature boxes, "sign here" labels, etc. `onBoxClick` fires with
 * {x_pct, y_pct} relative to the rendered page, for admin placement.
 *
 * `file` must be STABLE across re-renders (memoized here by its own
 * identity string) — react-pdf/pdfjs's worker transport gets torn down and
 * recreated if `<Document>` thinks the file changed on every render (a new
 * `{url, httpHeaders}` object literal on each parent render looks like a
 * "new file" to react-pdf), which is what produces the
 * "Cannot read properties of null (reading 'sendWithPromise')" crash under
 * React 19 StrictMode's double-invoked effects.
 */
export function PdfPageViewer({ file, pageNumber = 1, width = 560, onBoxClick, overlays = null }) {
  const wrapRef = useRef(null);
  const fileIdentity = typeof file === "string" ? file : file?.url || "";
  const stableFile = useMemo(() => file, [fileIdentity]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div
      ref={wrapRef}
      className="relative inline-block border border-neutral-300 bg-white select-none"
      style={onBoxClick ? { cursor: "crosshair" } : undefined}
      onClick={(e) => {
        if (!onBoxClick || !wrapRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        onBoxClick({ x_pct: (e.clientX - rect.left) / rect.width, y_pct: (e.clientY - rect.top) / rect.height });
      }}
      data-testid="pdf-page-viewer"
    >
      <Document key={fileIdentity} file={stableFile} options={PDF_LOAD_OPTIONS} loading={<div className="p-16 text-sm text-neutral-400">Loading PDF…</div>} error={<div className="p-16 text-sm text-red-500">Couldn't load this PDF.</div>}>
        <Page pageNumber={pageNumber} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
      </Document>
      {overlays}
    </div>
  );
}
